import { useGetIdentity, useLogout, type BaseKey } from '@refinedev/core';
import {
  Avatar,
  Typography,
  Space,
  Grid,
  Row,
  Col,
  Layout as AntdLayout,
  Button,
  theme,
  MenuProps,
  Dropdown,
} from 'antd';
import { useTheme } from '@admin-panel/config/theme';
import {
  FacilitySelect,
  type FacilitySelectValue,
} from '@admin-panel/components/lib/select/facility/facilityselect';
import { IconMoon, IconSun } from '@admin-panel/components/lib/icons';
import { FacilityType, IERPUser } from '@admin-panel/types';
import { SignOutIcon } from '@phosphor-icons/react';
import { PORTAL_NAME } from '@admin-panel/config/env';
import { ALL_FACILITIES_KEY, useGlobalContext } from '@admin-panel/hooks';
import { getNameInitials } from '@admin-panel/utils';
import { VendorSelector } from './VendorSelector';
// import NotificationsCenter from '../NofificationsCenter';
import { useStyles } from './styles';

const { Header: AntdHeader } = AntdLayout;
const { useToken } = theme;
const { Text } = Typography;
const { useBreakpoint } = Grid;

export const isSuperAdmin = (user?: IERPUser) =>
  Boolean(user?.roles?.some(role => role.name === 'SUPER_ADMIN'));

export const isNexusSuperAdmin = (user?: IERPUser) =>
  Boolean(user?.roles?.some(role => role.name === 'NEXUS_SUPER_ADMIN'));

export const isWarehouseAdmin = (user?: IERPUser) =>
  Boolean(user?.roles?.some(role => role.name === 'WAREHOUSE_ADMIN'));

const hasAllFacilitiesRole = (user?: IERPUser) =>
  Boolean(user?.roles?.some(role => role.name === 'ALL_FACILITIES'));

const canAccessAllFacilities = (user: IERPUser | undefined) => {
  if (!user) return false;
  return isSuperAdmin(user) || isWarehouseAdmin(user) || hasAllFacilitiesRole(user);
};

export const Header = () => {
  const { token } = useToken();
  const { styles } = useStyles();
  const { mode, setMode } = useTheme();
  const { data: user } = useGetIdentity<IERPUser>();
  const screens = useBreakpoint();
  const { mutate: logout } = useLogout();
  const showVendorSelector = PORTAL_NAME === 'vendor' || isNexusSuperAdmin(user);

  const items: MenuProps['items'] = [
    {
      key: 'logout',
      label: 'Logout',
      icon: <SignOutIcon />,
      onClick: () => logout(),
    },
  ];

  return (
    <AntdHeader
      style={{
        backgroundColor: token.colorBgElevated,
        padding: '0 24px',
        position: 'sticky',
        top: 0,
        zIndex: 4,
      }}
    >
      <Row
        align="stretch"
        style={{
          justifyContent: screens.sm ? 'space-between' : 'space-between',
          alignItems: 'center',
        }}
      >
        <Col xs={16} sm={8} md={12}>
          {PORTAL_NAME === 'warehouse' && <WarehouseFacilitySelector user={user} />}
          {showVendorSelector && <VendorSelector user={user} />}
        </Col>
        <Col xs={8} sm={16} md={12}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Space size={screens.md ? 32 : screens.sm ? 16 : 8} align="center">
              {screens.sm && (
                <Button
                  className={styles.themeSwitch}
                  type="text"
                  icon={mode === 'light' ? <IconMoon /> : <IconSun />}
                  onClick={() => {
                    setMode(mode === 'light' ? 'dark' : 'light');
                  }}
                />
              )}

              {/* {screens.sm && <NotificationsCenter />} */}

              <Space size={screens.md ? 16 : 8} align="center">
                {screens.sm && (
                  <Text ellipsis className={styles.userName}>
                    {user?.name}
                  </Text>
                )}
                <Dropdown menu={{ items }} placement="bottom" arrow={{ pointAtCenter: true }}>
                  <Space onClick={e => e.preventDefault()}>
                    {user?.profileImageUrl ? (
                      <Avatar size="large" src={user.profileImageUrl} alt={user?.name} />
                    ) : (
                      <Avatar size="large">{getNameInitials(user?.name)}</Avatar>
                    )}
                  </Space>
                </Dropdown>
              </Space>
            </Space>
          </div>
        </Col>
      </Row>
    </AntdHeader>
  );
};

export const WarehouseFacilitySelector = ({ user }: { user: IERPUser | undefined }) => {
  const { warehouseId, setWarehouseId, setAllFacilities } = useGlobalContext();
  const screens = useBreakpoint();

  const handleAllFacilitiesSelected = (facilityIds: Array<number>) => {
    setAllFacilities(facilityIds);
  };

  const toSingleValue = (selectedValue: FacilitySelectValue): BaseKey | undefined => {
    if (Array.isArray(selectedValue)) {
      return selectedValue[selectedValue.length - 1];
    }
    return selectedValue;
  };

  const handleWarehouseSelection = (selectedValue: FacilitySelectValue) => {
    const nextValue = toSingleValue(selectedValue);

    if (nextValue === undefined) {
      return;
    }

    if (typeof nextValue === 'string') {
      if (nextValue === ALL_FACILITIES_KEY) {
        setWarehouseId(ALL_FACILITIES_KEY);
      }
      return;
    }

    setWarehouseId(nextValue);
  };

  return (
    <FacilitySelect
      className="warehouse-working-facility"
      aria-label="Working facility"
      allowClear={false}
      status={warehouseId === undefined ? 'error' : ''}
      disabled={
        !user?.adminUserAttributes?.warehouseIds ||
        user?.adminUserAttributes?.warehouseIds.length === 1
      }
      value={warehouseId}
      style={{
        width: screens.xs ? '100%' : screens.sm ? 250 : 400,
        minWidth: 0,
      }}
      showSearch
      // Filter the already-loaded list locally: a server-side search would narrow the
      // options that "All Facilities" collects its ids from.
      clientSideSearch
      showAllFacilitiesOption={canAccessAllFacilities(user)}
      filters={[
        {
          field: 'id',
          operator: 'in',
          value: user?.adminUserAttributes?.warehouseIds || [],
        },
        {
          field: 'facilityType',
          operator: 'in',
          value: [FacilityType.WAREHOUSE, FacilityType.STORE],
        },
      ]}
      onChange={handleWarehouseSelection}
      onSelect={handleWarehouseSelection}
      onAllFacilitiesSelected={handleAllFacilitiesSelected}
    />
  );
};

export { VendorSelector };
